import { useState, useEffect, useMemo } from "react";
import { 
  AlertTriangle, 
  RefreshCw, 
  LogIn, 
  Send, 
  ShieldOff, 
  WifiOff, 
  ServerCrash,
  Loader2,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  MessageCircle,
  Building2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
// Apollo error type - using generic error type for compatibility
type QueryError = {
  message: string;
  graphQLErrors?: Array<{
    message: string;
    path?: readonly (string | number)[];
  }>;
  networkError?: {
    message?: string;
  } | null;
};
import { categorizeAuthError } from "@/contexts/AuthErrorContext";
import { useQuery, useMutation } from "@apollo/client/react";
import { GET_CURRENT_USER } from "@/graphql/queries";
import { SWITCH_ORGANIZATION } from "@/graphql/mutations";
import { setAuthCookie } from "@/lib/cookie-utils";
import { getScopedStorageItem, isImpersonating, removeScopedStorageItem, setScopedStorageItem } from "@/lib/session-utils";
import { clearTokens, saveTokens } from "@/lib/token-utils";

const DISCORD_WEBHOOK_URL = "https://discord.com/api/webhooks/1439869226390257737/6nDCoVK5O5S2894PID8c2iLAO23NibJ5Lp0w2MGgrnBVfSulod4JVVGJTzGQetwescav";

interface ApiErrorScreenProps {
  errors: (QueryError | undefined)[];
  /** The page/component where the error occurred */
  context?: string;
  /** Optional: Callback when refresh is clicked */
  onRefresh?: () => void;
  /** Optional: Callback when login is clicked */
  onLogin?: () => void;
}

/**
 * Analyzes GraphQL errors and returns appropriate UI configuration
 */
function analyzeErrors(errors: (QueryError | undefined)[]) {
  const actualErrors = errors.filter((e): e is QueryError => !!e);
  
  if (actualErrors.length === 0) {
    return null;
  }

  const messages: string[] = [];
  let errorType: 'auth' | 'network' | 'server' | 'unknown' = 'unknown';
  let isAuthError = false;
  let authErrorType: string | null = null;

  for (const error of actualErrors) {
    if (error.graphQLErrors) {
      for (const gqlError of error.graphQLErrors) {
        const categorized = categorizeAuthError(gqlError.message, gqlError.path as readonly string[] | undefined);
        if (categorized) {
          isAuthError = true;
          authErrorType = categorized.type;
          errorType = 'auth';
        }
        messages.push(gqlError.message);
      }
    }

    if (error.networkError) {
      errorType = 'network';
      messages.push(error.networkError.message || 'Network error');
    }

    // Also check the main error.message for categorization (in case graphQLErrors is empty)
    if (error.message && !authErrorType) {
      const categorized = categorizeAuthError(error.message);
      if (categorized) {
        isAuthError = true;
        authErrorType = categorized.type;
        errorType = 'auth';
      }
    }

    if (messages.length === 0 && error.message) {
      messages.push(error.message);
    }
  }

  let icon = AlertTriangle;
  let iconBg = 'bg-amber-500/20';
  let iconColor = 'text-amber-500';
  let title = 'Something went wrong';
  let description = 'We encountered an error while loading the dashboard.';

  if (errorType === 'auth' || isAuthError) {
    icon = ShieldOff;
    iconBg = 'bg-red-500/20';
    iconColor = 'text-red-500';
    title = 'Access Error';
    
    if (authErrorType === 'tenant_access') {
      description = 'You may not have access to this organization anymore, or there was an issue verifying your permissions.';
    } else if (authErrorType === 'user_not_found') {
      description = 'Your session could not be verified. This may happen if you were logged out or your account was modified.';
    } else {
      description = 'There was an authentication issue. Please try logging in again.';
    }
  } else if (errorType === 'network') {
    icon = WifiOff;
    iconBg = 'bg-blue-500/20';
    iconColor = 'text-blue-500';
    title = 'Connection Issue';
    description = 'Unable to connect to the server. Please check your internet connection and try again.';
  } else if (messages.some(m => m.includes('500') || m.includes('internal'))) {
    icon = ServerCrash;
    iconBg = 'bg-orange-500/20';
    iconColor = 'text-orange-500';
    title = 'Server Error';
    description = 'Our servers encountered an unexpected error. Please try again in a few moments.';
  }

  return {
    messages: [...new Set(messages)],
    errorType,
    isAuthError,
    authErrorType,
    icon,
    iconBg,
    iconColor,
    title,
    description,
  };
}

/**
 * A blocking full-screen error component that displays when critical API calls fail
 */
export function ApiErrorScreen({ 
  errors,
  context = "Dashboard",
  onRefresh,
  onLogin,
}: ApiErrorScreenProps) {
  const [showReportForm, setShowReportForm] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [additionalInfo, setAdditionalInfo] = useState("");
  const [email, setEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isAutoRecovering, setIsAutoRecovering] = useState(false);
  const [autoRecoveryAttempted, setAutoRecoveryAttempted] = useState(false);

  // Try to get user info for the report and for auto-recovery
  const { data: userData, loading: userLoading } = useQuery<{ me?: { email?: string; name?: string | null; organizations?: { id: string; name: string }[] } | null }>(GET_CURRENT_USER, {
    errorPolicy: 'all',
  });
  const currentUser = userData?.me;
  const userOrganizations = useMemo(() => currentUser?.organizations || [], [currentUser?.organizations]);
  
  // Mutation to switch organization
  const [switchOrganization] = useMutation(SWITCH_ORGANIZATION);
  
  const analysis = analyzeErrors(errors);

  // Auto-recovery: when tenant_access error occurs, try to switch to another valid organization
  useEffect(() => {
    const attemptAutoRecovery = async () => {
      // Only attempt recovery for tenant_access errors
      if (!analysis || analysis.authErrorType !== 'tenant_access') return;
      // Don't retry if we've already attempted
      if (autoRecoveryAttempted) return;
      // Wait for user data to load
      if (userLoading) return;
      // Need at least one organization to switch to
      if (!userOrganizations || userOrganizations.length === 0) return;
      
      setAutoRecoveryAttempted(true);
      setIsAutoRecovering(true);

      try {
        removeScopedStorageItem('defaultOrganizationId');
        
        const currentOrgId = getScopedStorageItem('defaultOrganizationId');
        const targetOrg = userOrganizations.find((org) => org.id !== currentOrgId) || userOrganizations[0];
        
        if (targetOrg) {
          console.log('[ApiErrorScreen] Auto-recovering: switching to organization', targetOrg.id, targetOrg.name);
          
          const result = await switchOrganization({
            variables: { organizationId: targetOrg.id }
          });
          
          const switchData = result.data as { switchOrganization?: { success: boolean; tokens?: { accessToken: string; refreshToken: string }; error?: string } } | null;
          if (switchData?.switchOrganization?.success) {
            const tokens = switchData.switchOrganization.tokens;
            if (tokens) {
              saveTokens(tokens.accessToken, tokens.refreshToken);
              setScopedStorageItem('defaultOrganizationId', targetOrg.id);
              if (!isImpersonating()) {
                setAuthCookie(tokens.accessToken);
              }
              
              console.log('[ApiErrorScreen] Auto-recovery successful, reloading...');
              window.location.reload();
              return;
            }
          }
        }
      } catch (err) {
        console.error('[ApiErrorScreen] Auto-recovery failed:', err);
      }
      
      setIsAutoRecovering(false);
    };

    attemptAutoRecovery();
  }, [analysis, userLoading, userOrganizations, autoRecoveryAttempted, switchOrganization]);
  
  if (!analysis) {
    return null;
  }

  // Show loading state while auto-recovering
  if (isAutoRecovering) {
    return (
      <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-background">
        <Card className="max-w-lg w-full p-8 space-y-6 border-border/50 shadow-2xl bg-card/95 backdrop-blur">
          <div className="flex justify-center">
            <img 
              src="/Logo White Transparent.png" 
              alt="FlowLabs" 
              className="h-8 w-auto opacity-60"
            />
          </div>
          <div className="flex flex-col items-center space-y-4">
            <div className="w-16 h-16 rounded-full bg-primary/20 flex items-center justify-center">
              <Building2 className="w-8 h-8 text-primary animate-pulse" />
            </div>
            <div className="text-center space-y-2">
              <h2 className="text-xl font-semibold">Switching Organization</h2>
              <p className="text-muted-foreground text-sm">
                Your access to the previous organization has changed. Switching to another organization...
              </p>
            </div>
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        </Card>
      </div>
    );
  }

  const { 
    messages, 
    isAuthError, 
    icon: Icon, 
    iconBg, 
    iconColor, 
    title,
    description,
  } = analysis;

  const handleRefresh = () => {
    if (onRefresh) {
      onRefresh();
    } else {
      window.location.reload();
    }
  };

  const handleLogin = () => {
    if (onLogin) {
      onLogin();
    } else {
      clearTokens();
      window.location.href = '/login';
    }
  };

  const handleReportError = async () => {
    setIsSubmitting(true);
    
    try {
      const errorDetails = messages.join('\n');
      const userEmail = email || currentUser?.email || 'Not provided';
      const userName = currentUser?.name || currentUser?.email || 'Unknown';
      const currentUrl = window.location.href;
      const userAgent = navigator.userAgent;
      const timestamp = new Date().toLocaleString();

      const webhookPayload = {
        embeds: [
          {
            title: "🚨 Dashboard Error Report",
            color: 0xEF4444, // Red
            fields: [
              {
                name: "Error Type",
                value: title,
                inline: true,
              },
              {
                name: "Context",
                value: context,
                inline: true,
              },
              {
                name: "User",
                value: userName,
                inline: true,
              },
              {
                name: "Contact Email",
                value: userEmail,
                inline: true,
              },
              {
                name: "Error Details",
                value: errorDetails.length > 1024 
                  ? errorDetails.substring(0, 1021) + "..." 
                  : errorDetails || 'No details available',
                inline: false,
              },
              ...(additionalInfo ? [{
                name: "User Description",
                value: additionalInfo.length > 1024 
                  ? additionalInfo.substring(0, 1021) + "..." 
                  : additionalInfo,
                inline: false,
              }] : []),
              {
                name: "Page URL",
                value: currentUrl,
                inline: false,
              },
              {
                name: "Reported At",
                value: timestamp,
                inline: true,
              },
              {
                name: "Browser",
                value: userAgent.length > 200 ? userAgent.substring(0, 197) + "..." : userAgent,
                inline: false,
              },
            ],
          },
        ],
      };

      const response = await fetch(DISCORD_WEBHOOK_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(webhookPayload),
      });

      if (!response.ok) {
        throw new Error("Failed to submit error report");
      }

      setIsSubmitted(true);
    } catch (error) {
      console.error("Error submitting report:", error);
      // Even if Discord fails, show success since user tried
      setIsSubmitted(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-[calc(100vh-80px)] flex items-center justify-center p-6 bg-background">
      <Card className="max-w-lg w-full p-8 space-y-6 border-border/50 shadow-2xl bg-card/95 backdrop-blur">
        {/* Logo */}
        <div className="flex justify-center">
          <img 
            src="/Logo White Transparent.png" 
            alt="FlowLabs" 
            className="h-8 w-auto opacity-60"
          />
        </div>

        {/* Error Icon */}
        <div className="flex justify-center">
          <div className={`w-24 h-24 rounded-full ${iconBg} flex items-center justify-center animate-pulse`}>
            <Icon className={`w-12 h-12 ${iconColor}`} />
          </div>
        </div>
        
        {/* Error Title & Message */}
        <div className="text-center space-y-3">
          <h1 className="text-3xl font-bold">{title}</h1>
          <p className="text-muted-foreground leading-relaxed">{description}</p>
        </div>

        {/* Technical Details (Collapsible) */}
        {messages.length > 0 && (
          <div className="border border-border/50 rounded-lg overflow-hidden">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="w-full px-4 py-3 flex items-center justify-between text-sm text-muted-foreground hover:bg-muted/50 transition-colors"
            >
              <span className="font-medium">Technical Details</span>
              {showDetails ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
            {showDetails && (
              <div className="px-4 py-3 bg-muted/30 border-t border-border/50">
                <code className="text-xs text-muted-foreground font-mono whitespace-pre-wrap break-all">
                  {messages.join('\n')}
                </code>
              </div>
            )}
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex flex-col gap-3">
          <div className="flex gap-3">
            <Button 
              onClick={handleRefresh} 
              className="flex-1" 
              size="lg"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh Page
            </Button>
            {isAuthError && (
              <Button 
                variant="outline" 
                onClick={handleLogin}
                className="flex-1"
                size="lg"
              >
                <LogIn className="w-4 h-4 mr-2" />
                Log In Again
              </Button>
            )}
          </div>

          {/* Report Issue Section */}
          {!showReportForm && !isSubmitted && (
            <Button 
              variant="ghost" 
              onClick={() => setShowReportForm(true)}
              className="w-full text-muted-foreground hover:text-foreground"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Report this issue
            </Button>
          )}
        </div>

        {/* Report Form */}
        {showReportForm && !isSubmitted && (
          <div className="space-y-4 pt-2 border-t border-border/50">
            <div className="text-center">
              <h3 className="font-semibold">Report this issue</h3>
              <p className="text-sm text-muted-foreground">
                Help us fix this by sharing some details
              </p>
            </div>

            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="report-email" className="text-sm">Your Email (for follow-up)</Label>
                <Input
                  id="report-email"
                  type="email"
                  placeholder={currentUser?.email || "you@example.com"}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="bg-muted/50"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="report-info" className="text-sm">What were you trying to do? (optional)</Label>
                <Textarea
                  id="report-info"
                  placeholder="Any additional context that might help us investigate..."
                  value={additionalInfo}
                  onChange={(e) => setAdditionalInfo(e.target.value)}
                  rows={3}
                  className="bg-muted/50"
                />
              </div>

              {/* Pre-filled error info */}
              <div className="p-3 bg-muted/30 rounded-lg text-xs">
                <p className="font-medium text-muted-foreground mb-1">The following will be included:</p>
                <ul className="text-muted-foreground space-y-0.5 list-disc list-inside">
                  <li>Error: {messages[0] || 'Unknown error'}</li>
                  <li>Page: {window.location.pathname}</li>
                  <li>Time: {new Date().toLocaleString()}</li>
                </ul>
              </div>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                onClick={() => setShowReportForm(false)}
                disabled={isSubmitting}
                className="flex-1"
              >
                Cancel
              </Button>
              <Button 
                onClick={handleReportError}
                disabled={isSubmitting}
                className="flex-1 bg-red-600 hover:bg-red-700"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Send Report
                  </>
                )}
              </Button>
            </div>
          </div>
        )}

        {/* Success Message */}
        {isSubmitted && (
          <div className="text-center space-y-3 pt-2 border-t border-border/50">
            <div className="flex justify-center">
              <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-green-500" />
              </div>
            </div>
            <div>
              <h3 className="font-semibold text-green-500">Report Sent!</h3>
              <p className="text-sm text-muted-foreground">
                Thank you for reporting this issue. Our team will investigate it shortly.
              </p>
            </div>
          </div>
        )}

        {/* Support Link */}
        <p className="text-xs text-muted-foreground text-center pt-2">
          Need immediate help?{" "}
          <a 
            href="https://discord.gg/tU3REeD83u" 
            target="_blank"
            rel="noopener noreferrer"
            className="text-primary hover:underline"
          >
            Join our Discord
          </a>
          {" "}or email{" "}
          <a 
            href="mailto:support@flowlabs.live" 
            className="text-primary hover:underline"
          >
            support@flowlabs.live
          </a>
        </p>
      </Card>
    </div>
  );
}

/**
 * Hook to check if any of the provided errors indicate a critical problem
 */
// eslint-disable-next-line react-refresh/only-export-components
export function useHasCriticalError(errors: (QueryError | undefined)[]): boolean {
  return errors.some(e => !!e);
}

export default ApiErrorScreen;
